/**
 * External dependencies
 */
import React, { useState, useEffect } from 'react';
import { Box, Heading, Text, Stack, Divider } from '@towersystems/towersystems-design-system';
import { connect } from 'react-redux';
import { get } from 'lodash';

/**
 * Internal dependencies
 */
import Main from 'components/main';
import Navigation from 'modules/dashboard/settings/navigation';
import AnnotatedSection from 'components/annotated-section';
import Card from 'components/card';
import FormInputCheckbox from 'components/forms/form-checkbox';
import { getAllRemotePreferences } from 'state/preferences/selectors';
import { savePreference, fetchPreferences } from 'state/preferences/actions';
import { successNotice } from 'state/notices/actions';
import AnnotatedSectionSkeleton from 'components/skeleton-loader/annotated-section-skeleton';

/**
 * Style dependencies
 */
import './styles.scss';

const DataSettings = ({ preferences, ...props }) => {
    const [values, setValues] = useState(preferences || {});
    const [requesting, setRequesting] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setRequesting(true);
            let p = await props.fetchPreferences();
            setValues(p);
        } catch (error) {}
        setRequesting(false);
    };

    const handleOnChangeCheckbox = async (e) => {
        let n = e.target.name;
        let v = !get(values, n);

        let b = { ...values };
        let updated = { ...values, [n]: v };
        setValues(updated);

        try {
            await props.savePreference(n, v);
            props.successNotice("Saved");
        } catch (error) {
            setValues(b);
        }
    };

    if (requesting) {
        return (
            <Main>
                <Stack space='large'>
                    <Navigation section='data' />
                    <AnnotatedSectionSkeleton />
                </Stack>
            </Main>
        );
    }

    return (
        <Main>
            <Stack space='large'>
                <Navigation section='data' />
                <Box paddingTop='xxlarge' paddingBottom='xxlarge'>
                    <Box display='flex' justifyContent='spaceBetween' alignItems='center' paddingBottom='xlarge'>
                        <Heading level='1'>Data Settings</Heading>
                    </Box>
                    <Divider />
                </Box>
                <Stack space='xlarge' dividers>
                    <AnnotatedSection>
                        <AnnotatedSection.Annotation
                            title='Data manipulation'
                            description='Choose how to manipulate received data before being sent to eCommerce platforms.'
                        />
                        <AnnotatedSection.Content>
                            <Stack space='large'>
                                <Card>
                                    <Card.Header heading='Department & category' />
                                    <Card.Section>
                                        <FormInputCheckbox
                                            label='Use categories as departments'
                                            onChange={handleOnChangeCheckbox}
                                            name='useDepartmentAsCategory'
                                            checked={get(values, 'useDepartmentAsCategory', false)}
                                        >
                                            <Text size='xsmall'>
                                                Your categories from Retailer will be imported as departments, your
                                                Retailer departments will be ignored.
                                            </Text>
                                        </FormInputCheckbox>
                                    </Card.Section>
                                    <Card.Section>
                                        <FormInputCheckbox
                                            label='Use Title Case'
                                            onChange={handleOnChangeCheckbox}
                                            name='capitalizeDepartmentName'
                                            checked={get(values, 'capitalizeDepartmentName', false)}
                                        >
                                            <Text size='xsmall'>
                                                Often Retailer sends Department and Category Titles in UPPER CASE. 
                                                Enabling this option will change the case of the Department and 
                                                Category Titles to Title Case. For example, HOME AND BUSINESS 
                                                SUPPLIES would be changed to Home And Business Supplies
                                            </Text>
                                        </FormInputCheckbox>
                                    </Card.Section>
                                </Card>
                                <Card>
                                    <Card.Header heading='Product' />
                                    <Card.Section>
                                        <FormInputCheckbox
                                            label='Use Title Case'
                                            onChange={handleOnChangeCheckbox}
                                            name='capitalizeProductName'
                                            checked={get(values, 'capitalizeProductName', false)}
                                        >
                                            <Text size='xsmall'>
                                                Often Retailer sends the product name in UPPER CASE. 
                                                Enabling this option will change the case of the product names to 
                                                Title Case. For example, CAT AND FIDDLE ORNAMENT would be changed 
                                                to Cat And Fiddle Ornament.
                                            </Text>
                                        </FormInputCheckbox>
                                    </Card.Section>
                                    <Card.Section>
                                        <FormInputCheckbox
                                            label='Display Rich Text Description'
                                            onChange={handleOnChangeCheckbox}
                                            name='displayInlineCss'
                                            checked={get(values, 'displayInlineCss', true)}
                                        >
                                            <Text size='xsmall'>
                                                When ticked, the rich text from Retailer product description will be used
                                                on your ecommerce platform
                                            </Text>
                                        </FormInputCheckbox>
                                    </Card.Section>
                                    <Card.Section>
                                        <FormInputCheckbox
                                            label='Remove Background Tag From Rich Text Description'
                                            onChange={handleOnChangeCheckbox}
                                            name='removeBackgroundTagFromInlineCss'
                                            checked={get(values, 'removeBackgroundTagFromInlineCss', true)}
                                        >
                                            <Text size='xsmall'>
                                                When ticked, the background tag from Retailer product rich text description will be removed
                                            </Text>
                                        </FormInputCheckbox>
                                    </Card.Section>
                                    <Card.Section>
                                        <FormInputCheckbox
                                            label='Remove Font Family From Rich Text Description'
                                            onChange={handleOnChangeCheckbox}
                                            name='removeFontFamilyFromInlineCss'
                                            checked={get(values, 'removeFontFamilyFromInlineCss', true)}
                                        >
                                            <Text size='xsmall'>
                                                When ticked, the font family from Retailer product rich text description will be removed
                                            </Text>
                                        </FormInputCheckbox>
                                    </Card.Section>
                                    <Card.Section>
                                        <FormInputCheckbox
                                            label='Remove Font Size From Rich Text Description'
                                            onChange={handleOnChangeCheckbox}
                                            name='removeFontSizeFromInlineCss'
                                            checked={get(values, 'removeFontSizeFromInlineCss', true)}
                                        >
                                            <Text size='xsmall'>
                                                When ticked, the font size from Retailer product rich text description will be removed
                                            </Text>
                                        </FormInputCheckbox>
                                    </Card.Section>
                                </Card>
                            </Stack>
                        </AnnotatedSection.Content>
                    </AnnotatedSection>
                </Stack>
            </Stack>
        </Main>
    );
};

export default connect((state) => ({ preferences: getAllRemotePreferences(state) }), {
    savePreference,
    fetchPreferences,
    successNotice
})(DataSettings);
